import { useState, useEffect } from 'react';
import { useAuthStore } from '@/stores/authStore';
import BranchSelector from '@/components/BranchSelector';
import { useCheckoutStore } from '@/stores/checkoutStore';
import { apiClient } from '@/api/client';
import { cacheProducts, searchProducts, getInventoryForBranch, queueSale } from '@/lib/db';
import { useInventorySync } from '@/hooks/useInventorySync';
import { Decimal } from 'decimal.js';

interface Product {
  productId: string;
  productName: string;
  category: string;
  price: string;
  zeroRated?: boolean;
}

export default function TillPage({
  onOpenStaffApp,
  onOpenCounter,
}: { onOpenStaffApp?: () => void; onOpenCounter?: () => void } = {}) {
  const { staff, logout, activeBranchId, activeBranchName } = useAuthStore();
  const checkout = useCheckoutStore();
  const [products, setProducts] = useState<Product[]>([]);
  const [searchQuery, setSearchQuery] = useState('');
  const [searchResults, setSearchResults] = useState<Product[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [showPaymentModal, setShowPaymentModal] = useState(false);
  const [cartOpen, setCartOpen] = useState(false);
  const [selectedPaymentMethod, setSelectedPaymentMethod] = useState<'CASH' | 'CARD' | 'MOBILE' | 'ACCOUNT'>('CASH');
  const [currentShiftId, setCurrentShiftId] = useState<string>('');

  useEffect(() => {
    initializeShift();
    loadProducts();
    checkout.clear(); // never carry a cart between shops
  }, [activeBranchId]);

  useEffect(() => {
    if (searchQuery.trim()) {
      filterProducts(searchQuery);
    } else {
      setSearchResults([]);
    }
  }, [searchQuery]);

  // Subscribe to real-time inventory updates
  useInventorySync(
    activeBranchId,
    (item) => {
      // Update product availability based on inventory status
      setProducts(prev =>
        prev.map(p =>
          p.productId === item.productId
            ? { ...p }
            : p,
        ),
      );
    },
  );

  const initializeShift = async () => {
    if (!activeBranchId) return;

    try {
      // Try to get current shift
      const response = await apiClient.getCurrentShift(activeBranchId);
      setCurrentShiftId(response.data.shiftId);
    } catch (error) {
      // No current shift, open a new one
      try {
        const openResponse = await apiClient.openShift(activeBranchId, '0.00');
        setCurrentShiftId(openResponse.data.shiftId);
      } catch (openError) {
        console.error('Failed to open shift:', openError);
      }
    }
  };

  const loadProducts = async () => {
    try {
      const response = await apiClient.getInventory(activeBranchId);
      const productList = response.data;
      setProducts(productList);
      await cacheProducts(
        productList.map((p: any) => ({
          id: p.productId,
          name: p.productName,
          category: p.category,
          price: p.price,
          zeroRated: Boolean(p.zeroRated),
          active: true,
        }))
      );
    } catch (error) {
      console.error('Failed to load products:', error);
    } finally {
      setIsLoading(false);
    }
  };

  const filterProducts = async (query: string) => {
    if (query.trim().length < 2) {
      setSearchResults([]);
      return;
    }

    const results = await searchProducts(query);
    setSearchResults(results as any);
  };

  const addToCart = (product: any) => {
    // Products arrive in two shapes: API ({productId, productName}) and offline cache ({id, name})
    checkout.addItem({
      productId: product.productId ?? product.id,
      productName: product.productName ?? product.name ?? '',
      category: product.category,
      quantity: 1,
      unitPrice: new Decimal(product.price),
      discountPercent: new Decimal(0),
      zeroRated: Boolean(product.zeroRated),
    });
    setSearchQuery('');
  };

  const handleCheckout = async () => {
    if (checkout.items.length === 0) {
      alert('Cart is empty');
      return;
    }

    if (!currentShiftId) {
      alert('No shift open. Please refresh the page.');
      return;
    }

    checkout.setPaymentMethod(selectedPaymentMethod);
    setShowPaymentModal(false);

    try {
      const sale = {
        branchId: activeBranchId,
        shiftId: currentShiftId,
        items: checkout.items.map(item => ({
          productId: item.productId,
          quantity: Math.floor(item.quantity),
          unitPrice: item.unitPrice.toFixed(2),
          discountPercent: item.discountPercent.toFixed(2),
        })),
        paymentMethod: selectedPaymentMethod,
      };

      if (apiClient.isOnline()) {
        // Sync online
        const response = await apiClient.createSale(activeBranchId, sale);
        console.log('Sale created:', response.data);
      } else {
        // Queue offline
        await queueSale(sale as any);
        console.log('Sale queued for sync');
      }
      checkout.clear();
      setCartOpen(false);
      alert('Sale completed successfully!');
    } catch (error: any) {
      console.error('Checkout failed:', error);
      // Surface the real reason — "try again" hides fixable problems
      const detail = error?.response?.data?.message;
      const text = Array.isArray(detail) ? detail.join('\n') : detail;
      alert(text ? `Checkout failed:\n${text}` : 'Checkout failed. Please try again.');
    }
  };

  if (isLoading) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-amber-500 mx-auto"></div>
          <p className="mt-4 text-gray-600">Loading till...</p>
        </div>
      </div>
    );
  }

  const displayProducts = searchQuery.trim() ? searchResults : products;

  return (
    <div className="flex flex-col lg:flex-row h-[100dvh] bg-gray-100 overflow-hidden">
      {/* Products Panel */}
      <div className="flex-1 flex flex-col min-h-0">
        {/* Header */}
        <div className="bg-white shadow px-3 py-2.5 lg:p-4 flex justify-between items-center gap-2">
          <div className="flex items-center gap-2.5 min-w-0">
            <img src="/mepco-badge.png" alt="" className="w-9 h-9 lg:w-11 lg:h-11 shrink-0" />
            <div className="min-w-0">
              <h1 className="text-lg lg:text-2xl font-bold text-amber-600 leading-tight">MepCo POS</h1>
              <div className="flex items-center gap-2 mt-0.5">
                <span className="text-xs lg:text-sm text-gray-600 truncate">{staff?.name}</span>
                <span className="text-gray-300">·</span>
                <BranchSelector />
              </div>
            </div>
          </div>
          <div className="flex items-center gap-2 shrink-0">
            {onOpenCounter && (
              <button
                onClick={onOpenCounter}
                className="bg-gray-100 hover:bg-gray-200 text-gray-800 text-sm px-3 py-2 rounded"
                title="Crates and prepayments"
              >
                📦 <span className="hidden sm:inline">Counter</span>
              </button>
            )}
            {onOpenStaffApp && (
              <button
                onClick={onOpenStaffApp}
                className="bg-gray-100 hover:bg-gray-200 text-gray-800 text-sm px-3 py-2 rounded"
                title="My shifts and tasks"
              >
                👤 <span className="hidden sm:inline">My shifts</span>
              </button>
            )}
            <button
              onClick={logout}
              className="bg-red-500 hover:bg-red-600 text-white text-sm px-3 py-2 rounded"
            >
              Logout
            </button>
          </div>
        </div>

        {/* Search Bar */}
        <div className="bg-white border-b p-4">
          <input
            type="text"
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            placeholder="Search products... (name, category)"
            className="input-field"
          />
        </div>

        {/* Products Grid */}
        <div className="flex-1 overflow-y-auto p-3 lg:p-4 pb-24 lg:pb-4">
          <div className="grid grid-cols-2 sm:grid-cols-3 xl:grid-cols-4 gap-2.5 lg:gap-3">
            {displayProducts.map((product: Product) => (
              <button
                key={(product as any).id ?? (product as any).productId}
                onClick={() => addToCart(product)}
                className="bg-white p-3 lg:p-4 rounded-lg shadow active:bg-amber-100 hover:shadow-lg hover:bg-amber-50 transition text-left min-h-[84px] flex flex-col justify-between"
              >
                <p className="font-semibold text-gray-800 text-sm lg:text-base leading-tight break-words">
                  {(product as any).name ?? (product as any).productName}
                </p>
                <p className="text-[11px] text-gray-500 capitalize">{product.category}</p>
                <p className="text-lg font-bold text-amber-600 mt-1 tabular-nums">R {product.price}</p>
              </button>
            ))}
          </div>
          {displayProducts.length === 0 && (
            <div className="text-center py-12">
              <p className="text-gray-500">
                {searchQuery ? 'No products found' : 'No products available'}
              </p>
            </div>
          )}
        </div>
      </div>

      {/* Collapsed cart bar — phones only, tap to open */}
      {!cartOpen && (
        <button
          onClick={() => setCartOpen(true)}
          className="lg:hidden fixed bottom-0 inset-x-0 z-40 bg-amber-500 text-white px-4 py-3 flex items-center justify-between shadow-[0_-2px_12px_rgba(0,0,0,0.15)]"
        >
          <span className="font-semibold">
            Cart · {checkout.items.length} {checkout.items.length === 1 ? 'item' : 'items'}
          </span>
          <span className="font-bold text-lg tabular-nums">R {checkout.total.toFixed(2)}</span>
        </button>
      )}

      {/* Cart Panel — bottom sheet on phones, sidebar on desktop */}
      <div
        className={`bg-white shadow-lg flex flex-col
          lg:w-80 lg:static lg:translate-y-0 lg:h-auto
          fixed inset-x-0 bottom-0 z-50 max-h-[85dvh] rounded-t-2xl lg:rounded-none
          transition-transform duration-200
          ${cartOpen ? 'translate-y-0' : 'translate-y-full lg:translate-y-0'}`}
      >
        {/* Cart Header */}
        <div className="bg-amber-500 text-white p-4 font-bold flex items-center justify-between">
          <span>Cart ({checkout.items.length} items)</span>
          <button
            onClick={() => setCartOpen(false)}
            className="lg:hidden text-white/90 text-sm font-medium px-2 py-1"
            aria-label="Close cart"
          >
            Close ▾
          </button>
        </div>

        {/* Cart Items */}
        <div className="flex-1 overflow-y-auto p-4 space-y-3">
          {checkout.items.length === 0 ? (
            <p className="text-center text-gray-500 py-8">Cart is empty</p>
          ) : (
            checkout.items.map((item) => (
              <div key={item.productId} className="bg-gray-50 p-3 rounded border">
                <div className="flex justify-between items-start">
                  <div className="flex-1">
                    <p className="font-semibold text-sm">{item.productName}</p>
                    <p className="text-xs text-gray-600">
                      {item.quantity} × R {item.unitPrice.toString()}
                    </p>
                  </div>
                  <button
                    onClick={() => checkout.removeItem(item.productId)}
                    className="text-red-500 hover:text-red-700 font-bold"
                  >
                    ✕
                  </button>
                </div>
                <div className="flex justify-between items-center mt-2 pt-2 border-t">
                  <input
                    type="number"
                    min="1"
                    value={item.quantity}
                    onChange={(e) =>
                      checkout.updateQuantity(item.productId, parseInt(e.target.value) || 1)
                    }
                    className="w-12 px-2 py-1 border rounded text-sm"
                  />
                  <p className="font-bold">R {item.price.toString()}</p>
                </div>
              </div>
            ))
          )}
        </div>

        {/* Totals */}
        <div className="bg-gray-50 p-4 border-t space-y-2 text-sm">
          <div className="flex justify-between">
            <span>Subtotal:</span>
            <span>R {checkout.subtotal.toString()}</span>
          </div>
          {checkout.discountAmount.gt(0) && (
            <div className="flex justify-between text-green-600">
              <span>Discount (10%):</span>
              <span>-R {checkout.discountAmount.toString()}</span>
            </div>
          )}
          {checkout.deliveryFee.gt(0) && (
            <div className="flex justify-between">
              <span>Delivery:</span>
              <span>R {checkout.deliveryFee.toString()}</span>
            </div>
          )}
          <div className="flex justify-between font-bold text-lg bg-amber-100 p-2 rounded border-t">
            <span>Total:</span>
            <span>R {checkout.total.toString()}</span>
          </div>
          {/* VAT sits inside the total, it is not added to it */}
          <div className="flex justify-between text-xs text-gray-500">
            <span>Includes VAT (15%)</span>
            <span>R {checkout.vat.toString()}</span>
          </div>
        </div>

        {/* Checkout Button */}
        <div className="p-4 border-t">
          <button
            onClick={() => setShowPaymentModal(true)}
            disabled={checkout.items.length === 0}
            className="w-full btn-primary disabled:opacity-50 disabled:cursor-not-allowed"
          >
            Complete Sale
          </button>
        </div>
      </div>

      {/* Payment Modal */}
      {showPaymentModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg shadow-xl p-6 max-w-md w-full">
            <h2 className="text-xl font-bold mb-4">Select Payment Method</h2>
            <div className="space-y-3">
              {(['CASH', 'CARD', 'MOBILE', 'ACCOUNT'] as const).map((method) => (
                <button
                  key={method}
                  onClick={() => setSelectedPaymentMethod(method)}
                  className={`w-full p-4 border-2 rounded-lg font-semibold transition ${
                    selectedPaymentMethod === method
                      ? 'border-amber-500 bg-amber-50'
                      : 'border-gray-200 hover:border-amber-300'
                  }`}
                >
                  {method}
                </button>
              ))}
            </div>
            <div className="mt-6 space-y-3">
              <button
                onClick={handleCheckout}
                className="w-full btn-primary"
              >
                Confirm Sale
              </button>
              <button
                onClick={() => setShowPaymentModal(false)}
                className="w-full btn-secondary"
              >
                Cancel
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
